import React, { useState } from 'react';
import { Text, InputCheckbox, Button } from '@companycam/slab-web';
import styled from 'styled-components';
import {
  GooglePlaceAutocomplete,
  getAddressComponent,
} from 'components/shared/GooglePlaceAutocomplete';
import { type GooglePlaceDetails } from 'data/v3/data-contracts';
import { translate } from 'javascripts/translations/i18n';
import { CompanyAddressStep } from 'components/publicProfile/CreatePublicProfileModal/CompanyAddressStep';
import { UseProfileFormReturn } from 'components/publicProfile/useProfileForm';

// #region styled-components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
  width: 100%;
`;

const CardContent = styled.div`
  margin-top: var(--cc_size_spacing_s);
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--cc_size_spacing_s);
  align-items: center;
  margin-top: var(--cc_size_spacing_m);
`;

const ContentBeforeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--cc_size_spacing_m);
`;

const GoogleConfirmationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: var(--cc_size_spacing_xl);
  align-items: center;
  background-color: var(--cc_color_background_2);
  padding: var(--cc_size_spacing_m);
  padding-right: var(--cc_size_spacing_s);
  border-radius: var(--cc_size_border_radius_l);
  flex-wrap: wrap;
`;

const StyledInputCheckbox = styled(InputCheckbox)`
  flex: 1;
  flex-basis: 50%;
`;
// #endregion

const getGoogleUrl = (placeId: string) =>
  `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${placeId}`;

export type GooglePlace = {
  place_id: string;
  name: string;
  formatted_address: string;
  formatted_phone_number: string;
  url: string;
  website: string;
};

type ReviewProfileSearchProps = {
  onCancel: () => void;
  onConfirm: () => void;
  className?: string;
  formMethods: UseProfileFormReturn;
};

const ReviewProfileSearch = ({
  onCancel,
  onConfirm,
  className,
  formMethods,
}: ReviewProfileSearchProps) => {
  const [listingConfirmed, setListingConfirmed] = useState(false);
  const [clickedLink, setClickedLink] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const resetForm = () => {
    setListingConfirmed(false);
    setClickedLink(false);
    formMethods.reset({
      name: '',
      email: '',
      googlePlaceId: '',
      phoneNumber: '',
      website: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
    });
  };

  const handleCancel = () => {
    setListingConfirmed(false);
    resetForm();
    onCancel();
    setSelectedPlaceId(null);
  };

  const handlePlaceChanged = (place: GooglePlaceDetails) => {
    setSelectedPlaceId(place.placeDetails.id);
    const details = place.placeDetails;
    resetForm();

    formMethods.setValue('name', details.displayName.text);
    formMethods.setValue('googlePlaceId', details.id);
    formMethods.setValue('phoneNumber', details.nationalPhoneNumber);

    if (details.websiteUri) {
      formMethods.setValue('website', new URL(details.websiteUri).origin);
    }

    // address
    if (details.addressComponents) {
      formMethods.setValue(
        'addressLine1',
        getAddressComponent(details.addressComponents, [
          'street_number',
          'route',
        ]),
      );
      formMethods.setValue(
        'addressLine2',
        getAddressComponent(details.addressComponents, ['subpremise']),
      );
      formMethods.setValue(
        'city',
        getAddressComponent(details.addressComponents, ['locality']),
      );
      formMethods.setValue(
        'state',
        getAddressComponent(details.addressComponents, [
          'administrative_area_level_1',
        ]),
      );
      formMethods.setValue(
        'postalCode',
        getAddressComponent(details.addressComponents, ['postal_code']),
      );
    }
  };

  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setListingConfirmed(e.target.checked);
    formMethods.trigger();
  };

  const isSubmitDisabled = !listingConfirmed || !formMethods.formState.isValid;

  return (
    <Container className={className}>
      <GooglePlaceAutocomplete
        testId="google-place-autocomplete"
        onPlaceSelect={handlePlaceChanged}
        placeholder={translate('project.reviews.reviewProfile.instructions')}
      />

      {selectedPlaceId ? (
        <div>
          <ContentBeforeContainer>
            <Text
              as="p"
              size="m"
              family="heading"
              weight={700}
              ccMargin="m none none none"
            >
              1️⃣{' '}
              {translate(
                'project.reviews.reviewProfile.confirmBusinessQuestion',
              )}
            </Text>
          </ContentBeforeContainer>
          <CardContent>
            <CompanyAddressStep formMethods={formMethods} />
            <Text
              as="p"
              family="heading"
              ccMargin="xl none m none"
              weight="700"
              size="m"
            >
              2️⃣ {translate('project.reviews.reviewProfile.googleConfirmTitle')}
            </Text>
            <GoogleConfirmationContainer>
              <StyledInputCheckbox
                design="default"
                id="id-for-testing"
                label={translate(
                  'project.reviews.reviewProfile.googleCheckboxLabel',
                )}
                onChange={handleCheckChange}
                status="normal"
                checked={listingConfirmed}
                value=""
                disabled={!clickedLink}
              />
              <Button
                data-testid="open-google-maps-button"
                design="minimal"
                href={getGoogleUrl(selectedPlaceId)}
                target="_blank"
                rel="noreferrer"
                color="primary"
                onClick={() => setClickedLink(true)}
                icon={{
                  name: 'open-in-new',
                  position: 'after',
                }}
              >
                {translate('project.reviews.reviewProfile.openGoogleMaps')}
              </Button>
            </GoogleConfirmationContainer>
            <ButtonsContainer>
              <Button color="subtle" onClick={handleCancel}>
                {translate('common.cancel')}
              </Button>
              <Button
                data-testid="create-profile-button"
                color="secondary"
                onClick={onConfirm}
                disabled={isSubmitDisabled}
              >
                {translate(
                  'project.reviews.reviewProfile.confirmBusinessButton',
                )}
              </Button>
            </ButtonsContainer>
          </CardContent>
        </div>
      ) : null}
    </Container>
  );
};

export default ReviewProfileSearch;
